import { useState, useEffect } from "react";
import { 
  Plus, Edit2, Trash2, Loader2, X, CheckCircle, AlertCircle,
  FileText, BarChart2, ClipboardList, Award, Receipt, Package, 
  CheckCircle2, Clock, File, FolderOpen, FileCheck, FilePlus,
  FileWarning, FileX, Briefcase, Building, Calendar, type LucideIcon
} from "lucide-react";
import { adminService, TipoDocumento } from "../../../services/adminService";

// Mapa de ícones disponíveis
const iconMap: Record<string, LucideIcon> = {
  "file-text": FileText,
  "bar-chart-2": BarChart2,
  "clipboard-list": ClipboardList,
  "award": Award,
  "receipt": Receipt,
  "package": Package,
  "check-circle": CheckCircle2,
  "clock": Clock,
  "file": File,
  "folder-open": FolderOpen,
  "file-check": FileCheck,
  "file-plus": FilePlus,
  "file-warning": FileWarning,
  "file-x": FileX,
  "briefcase": Briefcase,
  "building": Building,
  "calendar": Calendar,
};

// Lista de ícones para seleção
const availableIcons = [
  { name: "file-text", label: "Documento" },
  { name: "bar-chart-2", label: "Relatório" },
  { name: "clipboard-list", label: "Formulário" },
  { name: "award", label: "Certificado" },
  { name: "receipt", label: "Recibo/NF" },
  { name: "package", label: "Produto" },
  { name: "check-circle", label: "Aprovado" },
  { name: "clock", label: "Pendente" },
  { name: "file", label: "Arquivo" },
  { name: "folder-open", label: "Pasta" },
  { name: "file-check", label: "Verificado" },
  { name: "file-plus", label: "Novo Doc" },
  { name: "briefcase", label: "Negócio" },
  { name: "building", label: "Empresa" },
  { name: "calendar", label: "Agenda" },
];

// Componente para renderizar ícone dinâmico
function DynamicIcon({ name, className }: { name: string | null; className?: string }) {
  const IconComponent = name && iconMap[name] ? iconMap[name] : FileText;
  return <IconComponent className={className} />;
}

export function TipoDocumentoManagement() {
  const [tipos, setTipos] = useState<TipoDocumento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState<TipoDocumento | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [formData, setFormData] = useState({ nome: "", descricao: "", icone: "file-text", ordem: 0, isPmoc: false, isNf: false, isProposta: false });

  useEffect(() => { loadTipos(); }, []);

  const loadTipos = async () => {
    setIsLoading(true);
    try {
      const result = await adminService.listTiposDocumento();
      setTipos(result);
    } catch (error) {
      console.error("Erro ao carregar tipos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTipo) {
        await adminService.updateTipoDocumento(editingTipo.id, formData);
        setMessage({ type: "success", text: "Tipo atualizado com sucesso!" });
      } else {
        await adminService.createTipoDocumento(formData);
        setMessage({ type: "success", text: "Tipo criado com sucesso!" });
      }
      closeModal();
      loadTipos();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar tipo" });
    }
  };

  const handleEdit = (tipo: TipoDocumento) => {
    setEditingTipo(tipo);
    setFormData({ nome: tipo.nome, descricao: tipo.descricao || "", icone: tipo.icone || "file-text", ordem: tipo.ordem, isPmoc: tipo.isPmoc || false, isNf: tipo.isNf || false, isProposta: tipo.isProposta || false });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Deseja desativar este tipo de documento?")) return;
    try {
      await adminService.deleteTipoDocumento(id);
      setMessage({ type: "success", text: "Tipo desativado!" });
      loadTipos();
    } catch { setMessage({ type: "error", text: "Erro ao desativar tipo" }); }
  };

  const openNewModal = () => {
    setEditingTipo(null);
    setFormData({ nome: "", descricao: "", icone: "file-text", ordem: tipos.length + 1, isPmoc: false, isNf: false, isProposta: false });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingTipo(null); };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Tipos de Documento</h3>
        <button onClick={openNewModal} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Novo Tipo
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>
        ) : tipos.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Nenhum tipo cadastrado</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordem</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Docs</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tipos.map((tipo) => (
                <tr key={tipo.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-gray-600">{tipo.ordem}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <DynamicIcon name={tipo.icone} className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-gray-900">{tipo.nome}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-gray-600 text-sm">{tipo.descricao || "-"}</td>
                  <td className="px-4 py-4 text-gray-600">{tipo._count?.documents || 0}</td>
                  <td className="px-4 py-4">
                    <div className="flex gap-1">
                      {tipo.isPmoc && <span className="px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700">PMOC</span>}
                      {tipo.isNf && <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">NF</span>}
                      {tipo.isProposta && <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">Proposta</span>}
                      {!tipo.isPmoc && !tipo.isNf && !tipo.isProposta && <span className="text-gray-400 text-xs">-</span>}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${tipo.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                      {tipo.active ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(tipo)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(tipo.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{editingTipo ? "Editar Tipo" : "Novo Tipo"}</h3>
              <button onClick={closeModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input type="text" value={formData.nome} onChange={(e) => setFormData(p => ({ ...p, nome: e.target.value }))} required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <input type="text" value={formData.descricao} onChange={(e) => setFormData(p => ({ ...p, descricao: e.target.value }))} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ícone</label>
                <div className="grid grid-cols-5 gap-2">
                  {availableIcons.map((icon) => {
                    const IconComp = iconMap[icon.name];
                    return (
                      <button key={icon.name} type="button" onClick={() => setFormData(p => ({ ...p, icone: icon.name }))}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-colors ${
                          formData.icone === icon.name 
                            ? "border-blue-600 bg-blue-50 text-blue-600" 
                            : "border-gray-200 hover:border-gray-300 text-gray-600"
                        }`}
                        title={icon.label}>
                        <IconComp className="w-5 h-5" />
                        <span className="text-[10px] truncate w-full text-center">{icon.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ordem</label>
                <input type="number" value={formData.ordem} onChange={(e) => setFormData(p => ({ ...p, ordem: parseInt(e.target.value) || 0 }))} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div className="grid grid-cols-3 gap-4 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.isPmoc} onChange={(e) => setFormData(p => ({ ...p, isPmoc: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                  <span className="text-sm text-gray-700">PMOC</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.isNf} onChange={(e) => setFormData(p => ({ ...p, isNf: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                  <span className="text-sm text-gray-700">Nota Fiscal</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.isProposta} onChange={(e) => setFormData(p => ({ ...p, isProposta: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                  <span className="text-sm text-gray-700">Proposta</span>
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">Cancelar</button>
                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
